import React from "react";
import { useIntl } from "react-intl";
import Icon from "@Components/Icon";
import { getStoreStatus } from "@Src_main/utils/storeHoursUtils";
import { useGmbOpeningHours } from "@Src_main/utils/useGmbOpeningHours";
import { formatTime } from "@Src_main/utils/gmbOpeningHoursUtils";

/**
 * Same open/closed status and today/tomorrow hours shown on each store in the selector.
 */
export default function StoreAvailability(_ref) {
  var {
    store,
    centered = false,
    narrative = false,
    storeName
  } = _ref;
  var {
    formatMessage
  } = useIntl();
  var gmbLocationId = (store === null || store === void 0 ? void 0 : store.gmbLocationId) || null;
  var {
    storeStatus: gmbStoreStatus,
    loading: gmbLoading,
    hasGmbData
  } = useGmbOpeningHours(gmbLocationId);
  var regularStoreStatus = getStoreStatus(store === null || store === void 0 ? void 0 : store.store_hours);
  var closedLabel = formatMessage({
    id: "storeHours.closed"
  });
  var rangeSeparator = narrative ? " - " : "-";
  var formatHoursForDisplay = effectiveHours => {
    if (!effectiveHours || effectiveHours.type === "closed") {
      return null;
    }
    if (effectiveHours.periods && effectiveHours.periods.length > 0) {
      return effectiveHours.periods.map(period => "".concat(formatTime(period.openTime)).concat(rangeSeparator).concat(formatTime(period.closeTime))).join(" / ");
    }
    return null;
  };
  var isOpen;
  var todayDisplay;
  var tomorrowDisplay;
  if (hasGmbData && gmbStoreStatus) {
    isOpen = gmbStoreStatus.isOpen;
    todayDisplay = formatHoursForDisplay(gmbStoreStatus.todayHours);
    tomorrowDisplay = formatHoursForDisplay(gmbStoreStatus.tomorrowHours);
  } else if (regularStoreStatus) {
    isOpen = regularStoreStatus.isOpen;
    todayDisplay = regularStoreStatus.todayHours && regularStoreStatus.todayHours.length > 0 ? regularStoreStatus.todayHours.map(slot => "".concat(slot.open || "").concat(rangeSeparator).concat(slot.close || "")).join(" / ") : null;
    tomorrowDisplay = regularStoreStatus.tomorrowHours && regularStoreStatus.tomorrowHours.length > 0 ? regularStoreStatus.tomorrowHours.map(slot => "".concat(slot.open || "").concat(rangeSeparator).concat(slot.close || "")).join(" / ") : null;
  } else {
    isOpen = false;
    todayDisplay = null;
    tomorrowDisplay = null;
  }
  var isLoadingGmbHours = gmbLoading && gmbLocationId;
  var align = centered ? "uk-flex-center uk-text-center" : "uk-flex-left";
  var todayLabel = todayDisplay || closedLabel;
  var tomorrowLabel = tomorrowDisplay || closedLabel;
  if (narrative) {
    var name = storeName || (store === null || store === void 0 ? void 0 : store.frontend_name) || (store === null || store === void 0 ? void 0 : store.name) || "";
    return /*#__PURE__*/React.createElement("div", {
      className: "uk-text-center",
      "data-testid": "store-availability"
    }, isLoadingGmbHours ? /*#__PURE__*/React.createElement("div", {
      className: "uk-flex uk-flex-center uk-flex-middle",
      style: {
        minHeight: "50px"
      }
    }, /*#__PURE__*/React.createElement("div", {
      "uk-spinner": "ratio: 0.6"
    })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
      className: "uk-text-bold uk-margin-small-bottom"
    }, formatMessage({
      id: isOpen ? "menu.preorder.closed.storeOpen" : "menu.preorder.closed.storeClosed"
    }, {
      store: name
    })), /*#__PURE__*/React.createElement("div", {
      className: "uk-text-left uk-text-small uk-text-dimgray",
      style: {
        textAlign: "left"
      }
    }, /*#__PURE__*/React.createElement("p", {
      className: "uk-margin-remove"
    }, todayDisplay ? /*#__PURE__*/React.createElement(React.Fragment, null, formatMessage({
      id: "menu.preorder.closed.todayOpen"
    }), " ", /*#__PURE__*/React.createElement("span", {
      className: "uk-text-nowrap"
    }, todayDisplay)) : formatMessage({
      id: "menu.preorder.closed.todayClosed"
    })), /*#__PURE__*/React.createElement("p", {
      className: "uk-margin-remove",
      style: {
        marginTop: 2
      }
    }, tomorrowDisplay ? /*#__PURE__*/React.createElement(React.Fragment, null, formatMessage({
      id: "menu.preorder.closed.tomorrowOpen"
    }), " ", /*#__PURE__*/React.createElement("span", {
      className: "uk-text-nowrap"
    }, tomorrowDisplay)) : formatMessage({
      id: "menu.preorder.closed.tomorrowClosed"
    })))));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "uk-text-dimgray store-hours ".concat(centered ? "uk-text-center" : ""),
    "data-testid": "store-availability"
  }, isLoadingGmbHours ? /*#__PURE__*/React.createElement("div", {
    className: "uk-flex uk-flex-center uk-flex-middle",
    style: {
      minHeight: "50px"
    }
  }, /*#__PURE__*/React.createElement("div", {
    "uk-spinner": "ratio: 0.6"
  })) : /*#__PURE__*/React.createElement("div", {
    className: "restaurant-hours uk-text-xsmall uk-flex uk-flex-column",
    style: {
      animation: "none"
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "uk-flex uk-flex-row ".concat(align, " uk-flex-middle uk-text-bold ").concat(isOpen ? "uk-text-success-alt" : "uk-text-emphasis"),
    style: {
      animation: "none"
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "icon-shipping",
    style: {
      animation: "none"
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "uk-padding-small uk-padding-remove-vertical uk-padding-remove-right"
  }), /*#__PURE__*/React.createElement("div", null, isOpen ? formatMessage({
    id: "storeHours.openNow"
  }) : formatMessage({
    id: "storeHours.closedNow"
  }))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", null, formatMessage({
    id: "storeHours.today"
  }), ": ", todayLabel), /*#__PURE__*/React.createElement("div", null, formatMessage({
    id: "storeHours.tomorrow"
  }), ": ", tomorrowLabel))));
}